import classNames from "classnames";
import { gql } from "@apollo/client";
import {
  CreditPlanEnum,
  Maybe,
  OrganizationSubscriptionTierEnum,
  ProfileCompanyCreditsCard_UserAccountFragment,
} from "../../__generated__/graphql";
import NumberFormat from "react-number-format";
import CoinIcon from "../../assets/img/icons/CoinIcon";
import CiroLink from "../shared/CiroLink";
import {
  STRIPE_CUSTOMER_PORTAL_LINK,
  PRICING_PAGE_LINK,
} from "../../utils/consts";
import LinkArrowIcon from "../../assets/img/icons/LinkArrowIcon";
import CiroCoinPill from "../shared/CiroCoinPill";
import { STRIPE_LINKS } from "../../clientSideVariables";

export const ProfileCompanyCreditsCard_UserAccount = gql`
  fragment ProfileCompanyCreditsCard_UserAccount on UserAccount {
    pk
    email
    impersonating {
      pk
      email
    }
    org {
      subscription_tier
      credit_plan {
        incremental_credits
        plan
        balance
      }
    }
  }
`;

const CREDITS_PLAN_TO_SCHEDULE = {
  [CreditPlanEnum.Annual]: "annually",
  [CreditPlanEnum.Monthly]: "monthly",
  [CreditPlanEnum.NonRecurring]: "one time",
  [CreditPlanEnum.Quarterly]: "quarterly",
};

interface IProfileCompanyCreditsCardProps {
  userAccount?: Maybe<ProfileCompanyCreditsCard_UserAccountFragment>;
}

const ProfileCompanyCreditsCard = ({
  userAccount,
}: IProfileCompanyCreditsCardProps) => {
  if (!userAccount?.org?.credit_plan) {
    return null;
  }
  const creditPlan = userAccount?.org?.credit_plan;
  const urlAppend =
    `?client_reference_id=${userAccount?.impersonating?.pk || userAccount?.pk}` +
    `&prefilled_email=${userAccount?.impersonating?.email || userAccount?.email}`;

  const stripeTopUpLink =
    creditPlan.incremental_credits && creditPlan.incremental_credits >= 15000
      ? STRIPE_LINKS.plus + urlAppend
      : creditPlan.incremental_credits &&
          creditPlan.incremental_credits < 15000 &&
          creditPlan.incremental_credits > 5000
        ? STRIPE_LINKS.between + urlAppend
        : STRIPE_LINKS.starter + urlAppend;

  const isRecurringPlan =
    creditPlan.plan === CreditPlanEnum.Monthly ||
    creditPlan.plan === CreditPlanEnum.Annual;

  const managementLinkHref = isRecurringPlan
    ? STRIPE_CUSTOMER_PORTAL_LINK
    : PRICING_PAGE_LINK;
  const managementLinkText = isRecurringPlan
    ? "Upgrade, downgrade, or cancel your subscription"
    : "View pricing plans";

  return (
    <div>
      <div
        className={classNames("flex", "text-sm", "items-center", "pb-1", {
          "pb-3":
            userAccount?.org?.subscription_tier ===
            OrganizationSubscriptionTierEnum.Free,
        })}
      >
        <div className={classNames("font-semibold", "pr-2")}>Credits</div>
        <CiroCoinPill amount={creditPlan.balance || 0} />
      </div>
      {userAccount?.org?.subscription_tier !==
        OrganizationSubscriptionTierEnum.Free && (
        <div>
          <CiroLink href={stripeTopUpLink}>
            <div
              className={classNames(
                "flex",
                "items-center",
                "text-sm",
                "pt-1",
                "pb-3",
              )}
            >
              <span>Buy one-time top-up</span>
              <span className="pl-2">
                <LinkArrowIcon />
              </span>
            </div>
          </CiroLink>
        </div>
      )}
      <div
        className={classNames(
          "border-t-1",
          "-mx-4",
          "p-4",
          "bg-neutral-50",
          "rounded-b-xl",
        )}
      >
        <div className={classNames("flex", "font-semibold", "text-sm", "pb-2")}>
          Credits schedule
        </div>
        <div className={classNames("flex", "items-center", "text-sm")}>
          <div className={classNames("text-amber-500", "pr-2")}>
            <CoinIcon />
          </div>
          +
          <NumberFormat
            displayType="text"
            value={creditPlan.incremental_credits}
            thousandSeparator=","
          />
          &nbsp;new credits given{" "}
          {
            CREDITS_PLAN_TO_SCHEDULE[
              creditPlan.plan || CreditPlanEnum.NonRecurring
            ]
          }
        </div>
        <div className={classNames("mt-2")}>
          <CiroLink href={managementLinkHref}>
            <div className={classNames("flex", "items-center", "text-sm")}>
              <span>{managementLinkText}</span>
              <span className="pl-2">
                <LinkArrowIcon />
              </span>
            </div>
          </CiroLink>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompanyCreditsCard;
